import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Copy, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, Settings2, Clock,
  CalendarDays, ListChecks, ClipboardList, Palette, Bell, AlertTriangle, Video, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { RichTextEditor } from "@/components/common/RichTextEditor";

const DOW = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const TZS = [
  ["America/Los_Angeles", "Pacific (Los Angeles)"], ["America/Denver", "Mountain (Denver)"],
  ["America/Chicago", "Central (Chicago)"], ["America/New_York", "Eastern (New York)"],
  ["America/Phoenix", "Arizona (Phoenix)"], ["America/Anchorage", "Alaska"], ["Pacific/Honolulu", "Hawaii"],
];
const METHODS = [["phone", "Phone"], ["in_person", "In Person"], ["teams", "Microsoft Teams"], ["custom", "Custom Instructions"]];
const FIELD_REQ = [["required", "Required"], ["optional", "Optional"], ["hidden", "Hidden"]];
const AUDIENCES = [["public_self_book", "Public"], ["approved_recruiters", "Authorized Recruiters Only"], ["internal_staff", "Internal Only"], ["signed_in_users", "All Signed-In Users"], ["member_roles", "Selected Member Roles"]];
const MEMBER_ROLES = [["candidate", "Candidate"], ["agent", "Agent"], ["agent_staff", "Agent Staff"], ["referral_partner", "Referral Partner"], ["external_recruiter", "Outside Recruiter"], ["internal_recruiter", "Internal Recruiter"], ["recruiting_support", "Support Staff"], ["recruiting_manager", "Recruiting Manager"], ["group_admin", "Group Admin"], ["account_admin", "Account Admin"]];
const AUDIENCE_HELP = {
  public_self_book: "Shown on your public Scheduling Page. Signed-in users may also book it when permitted.",
  approved_recruiters: "Never shown publicly. Only signed-in recruiters with Booking Access to this type can book it.",
  internal_staff: "Never shown publicly. Only authorized internal Top-Recruit staff can book it (not Outside Recruiters).",
  signed_in_users: "Never shown publicly. Any signed-in Top-Recruit user who belongs to one of your organizations can book it for themselves.",
  member_roles: "Never shown publicly. Only signed-in users holding one of the selected roles in one of your organizations can book it.",
};
const PHONE_REQ = [["required", "Required"], ["optional", "Optional"]];
const NOTICE = [[0, "No minimum"], [60, "1 hour"], [120, "2 hours"], [240, "4 hours"], [720, "12 hours"], [1440, "24 hours"], [2880, "48 hours"]];
const HORIZON = [[7, "7 days"], [14, "14 days"], [30, "30 days"], [45, "45 days"], [60, "60 days"], [90, "90 days"]];
const BUFFER = [[0, "None"], [5, "5 minutes"], [10, "10 minutes"], [15, "15 minutes"], [30, "30 minutes"]];
const DURATIONS = [[15, "15 min"], [20, "20 min"], [30, "30 min"], [45, "45 min"], [60, "60 min"], [90, "90 min"]];
const SLOT_INTERVALS = [[5, "Every 5 min"], [10, "Every 10 min"], [15, "Every 15 min"], [20, "Every 20 min"], [30, "Every 30 min"], [60, "Every 60 min"]];
const OPTIONAL_FIELDS = [["city", "City"], ["state", "State"], ["occupation", "Current Occupation"], ["interested_in", "Interested In"], ["comments", "What would you like to discuss?"]];

const TABS = [
  ["general", "General", Settings2], ["types", "Appointment Types", ListChecks],
  ["availability", "Availability", CalendarDays], ["rules", "Booking Rules", Clock],
  ["form", "Form", ClipboardList], ["branding", "Branding", Palette], ["notifications", "Notifications", Bell],
];

const uid = () => Math.random().toString(36).slice(2, 10);

export default function SchedulingPageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("general");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/scheduling-pages/${id}`).then((r) => setP(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/calendar/scheduling-pages"); }); }, [id]);

  const set = (patch) => setP((x) => ({ ...x, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: p.name, slug: p.slug, active: p.active, intro: p.intro, timezone: p.timezone,
        branding: p.branding, appointment_types: p.appointment_types, availability: p.availability,
        overrides: p.overrides, blocks: p.blocks, rules: p.rules, form: p.form, notifications: p.notifications,
        interest_options: p.interest_options,
      };
      const { data } = await api.patch(`/scheduling-pages/${id}`, body);
      setP((x) => ({ ...data, microsoft_connected: x.microsoft_connected }));
      toast.success("Scheduling page saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const copyLink = () => { navigator.clipboard?.writeText(p.public_url); toast.success("Scheduling link copied"); };

  if (!p) return <p className="text-slate-400 py-16 text-center">Loading…</p>;

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/calendar/scheduling-pages")} className="text-sm text-slate-400 hover:text-slate-600 inline-flex items-center gap-1 mb-2" data-testid="editor-back"><ArrowLeft className="w-4 h-4" />Scheduling Pages</button>
      <PageHeader title={p.name || "Scheduling Page"} description="Configure your public booking experience."
        actions={<div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="editor-copy"><Copy className="w-3.5 h-3.5 mr-1" />Copy Link</Button>
          <Button variant="outline" size="sm" onClick={() => window.open(p.public_url, "_blank")} data-testid="editor-preview"><ExternalLink className="w-3.5 h-3.5 mr-1" />Preview</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="editor-save">{saving ? "Saving…" : "Save"}</Button>
        </div>} />

      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {TABS.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`editor-tab-${k}`}
            className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <I className="w-4 h-4" />{l}
          </button>
        ))}
      </div>

      {tab === "general" && <GeneralTab p={p} set={set} id={id} copyLink={copyLink} />}
      {tab === "types" && <TypesTab p={p} set={set} />}
      {tab === "availability" && <AvailabilityTab p={p} set={set} />}
      {tab === "rules" && <RulesTab p={p} set={set} />}
      {tab === "form" && <FormTab p={p} set={set} />}
      {tab === "branding" && <BrandingTab p={p} set={set} />}
      {tab === "notifications" && <NotificationsTab p={p} set={set} />}

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="editor-save-bottom">{saving ? "Saving…" : "Save Changes"}</Button>
      </div>
    </div>
  );
}

function Panel({ title, description, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm mb-4">
      <div className="px-5 pt-5"><h3 className="font-semibold text-slate-800">{title}</h3>{description && <p className="text-sm text-slate-400 mt-0.5">{description}</p>}</div>
      <div className="px-5 py-4 space-y-4">{children}</div>
    </div>
  );
}
function Fld({ label, children }) { return <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</Label>{children}</div>; }

function RuleSelect({ label, value, options, onChange, tid }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold text-slate-500 mb-1 block">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={tid} className="h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function GeneralTab({ p, set, id, copyLink }) {
  const [slugState, setSlugState] = useState(null);
  const checkSlug = async (v) => {
    set({ slug: v });
    if (!v) { setSlugState(null); return; }
    try { const { data } = await api.get("/scheduling-pages/check-slug", { params: { slug: v, exclude: id } }); setSlugState(data); }
    catch { setSlugState(null); }
  };
  return (
    <Panel title="General" description="Basic details and your public URL.">
      <Fld label="Page Name"><Input value={p.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid="gen-name" /></Fld>
      <Fld label="Public URL slug">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400 shrink-0">/schedule/</span>
          <Input value={p.slug || ""} onChange={(e) => checkSlug(e.target.value)} data-testid="gen-slug" />
        </div>
        {slugState && (slugState.available
          ? <p className="text-xs text-emerald-600 mt-1" data-testid="slug-ok">Available</p>
          : <p className="text-xs text-amber-600 mt-1" data-testid="slug-taken">Taken — we'll use <b>{slugState.suggestion}</b> instead.</p>)}
      </Fld>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-400">Public URL:</span>
        <span className="text-slate-700 truncate" data-testid="gen-public-url">{p.public_url}</span>
        <button onClick={copyLink} className="text-brand" data-testid="gen-copy"><Copy className="w-3.5 h-3.5" /></button>
        <button onClick={() => window.open(p.public_url, "_blank")} className="text-brand" data-testid="gen-open"><ExternalLink className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
        <div><p className="text-sm font-medium text-slate-700">Active</p><p className="text-xs text-slate-400">When off, the public page shows an "unavailable" message.</p></div>
        <Switch checked={!!p.active} onCheckedChange={(v) => set({ active: v })} data-testid="gen-active" />
      </div>
      <Fld label="Public introductory heading"><Input value={p.name || ""} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Schedule a Conversation" data-testid="gen-heading" /></Fld>
      <Fld label="Public introductory text"><RichTextEditor value={p.intro || ""} onChange={(v) => set({ intro: v })} testId="gen-intro" minHeight={130} placeholder="A short welcome message shown to visitors. Use bold, bullets or links to make it clear." /></Fld>
      <Fld label="Recruiter timezone">
        <Select value={p.timezone || "America/Los_Angeles"} onValueChange={(v) => set({ timezone: v })}>
          <SelectTrigger data-testid="gen-timezone"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{TZS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </Fld>
    </Panel>
  );
}

function TypesTab({ p, set }) {
  const types = p.appointment_types || [];
  const upd = (i, patch) => set({ appointment_types: types.map((t, j) => j === i ? { ...t, ...patch } : t) });
  const add = () => set({ appointment_types: [...types, { id: uid(), name: "New Appointment Type", description: "", duration_min: 30, method: "phone", method_instructions: "", active: true, stage_on_book: "", allow_static_link: false, field_rules: { notes: "optional", resume: "optional", interested_in: "optional", phone: "required" } }] });
  const remove = (i) => set({ appointment_types: types.filter((_, j) => j !== i) });
  const move = (i, d) => { const n = [...types]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; set({ appointment_types: n }); };
  return (
    <Panel title="Appointment Types" description="What visitors can book. Reorder, archive, or add types.">
      {types.map((t, i) => (
        <div key={t.id} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`type-row-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={t.name} onChange={(e) => upd(i, { name: e.target.value })} className="font-medium" data-testid={`type-name-${i}`} />
            <button onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-600" data-testid={`type-up-${i}`}><ChevronUp className="w-4 h-4" /></button>
            <button onClick={() => move(i, 1)} className="text-slate-400 hover:text-slate-600" data-testid={`type-down-${i}`}><ChevronDown className="w-4 h-4" /></button>
            <button onClick={() => remove(i)} className="text-rose-400 hover:text-rose-600" data-testid={`type-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-[220px_1fr] gap-3 items-start rounded-lg border border-slate-200 p-3" data-testid={`type-audience-box-${i}`}>
            <RuleSelect label="Audience / Booking Visibility" value={t.booking_access === "hidden_group" ? "internal_staff" : (t.booking_access || "public_self_book")} options={AUDIENCES} onChange={(v) => upd(i, { booking_access: v })} tid={`type-audience-${i}`} />
            <div className="text-[11px] text-slate-500 pt-5">
              {AUDIENCE_HELP[t.booking_access || "public_self_book"] || AUDIENCE_HELP.internal_staff}
              {["approved_recruiters", "signed_in_users", "member_roles"].includes(t.booking_access) && p.user_id && (
                <button type="button" className="block mt-1 text-brand font-medium hover:underline" data-testid={`type-recruiter-link-${i}`}
                  onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/meetings/book?host=${p.user_id}&type=${t.id}`); toast.success("Booking link copied — requires sign-in"); }}>
                  Copy sign-in booking link
                </button>
              )}
              {t.booking_access === "member_roles" && (
                <div className="mt-2" data-testid={`type-allowed-roles-${i}`}>
                  <p className="text-xs font-semibold text-slate-700 mb-1">Allowed Member Roles</p>
                  <div className="grid grid-cols-2 gap-1">
                    {MEMBER_ROLES.map(([k, l]) => {
                      const sel = (t.allowed_roles || []).includes(k);
                      return (<label key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                        <input type="checkbox" checked={sel} data-testid={`type-role-${i}-${k}`}
                          onChange={() => upd(i, { allowed_roles: sel ? t.allowed_roles.filter((x) => x !== k) : [...(t.allowed_roles || []), k] })} />{l}</label>);
                    })}
                  </div>
                  {!(t.allowed_roles || []).length && <p className="text-[11px] text-rose-600 mt-1">Select at least one role.</p>}
                </div>
              )}
            </div>
          </div>
          <Fld label="Public Description"><RichTextEditor value={t.description || ""} onChange={(v) => upd(i, { description: v })} testId={`type-desc-${i}`} minHeight={120} placeholder="What this appointment is for. Formatting supported." /></Fld>
          <div className="grid sm:grid-cols-3 gap-3">
            <Fld label="Duration">
              <Select value={String(t.duration_min)} onValueChange={(v) => upd(i, { duration_min: Number(v) })}>
                <SelectTrigger data-testid={`type-duration-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{DURATIONS.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Start-time interval">
              <Select value={String(t.slot_interval_min || 15)} onValueChange={(v) => upd(i, { slot_interval_min: Number(v) })}>
                <SelectTrigger data-testid={`type-interval-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{SLOT_INTERVALS.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Meeting Method">
              <Select value={t.method || "phone"} onValueChange={(v) => upd(i, { method: v })}>
                <SelectTrigger data-testid={`type-method-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
          </div>
          <p className="text-[11px] text-slate-400 -mt-1">Start times are offered every <b>{t.slot_interval_min || 15} min</b>; each booking still reserves the full {t.duration_min}-min duration.</p>
          <Fld label="Location / Instructions (optional)"><RichTextEditor value={t.method_instructions || ""} onChange={(v) => upd(i, { method_instructions: v })} testId={`type-instructions-${i}`} minHeight={100} placeholder="e.g. office address, or call instructions. Calendar invites use the plain-text version." /></Fld>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`type-fieldrules-${i}`}>
            <p className="text-xs font-semibold text-slate-600">Field requirements for this appointment type</p>
            <div className="grid grid-cols-2 gap-2">
              <RuleSelect label="Interested In" value={(t.field_rules || {}).interested_in || "optional"} options={FIELD_REQ} onChange={(v) => upd(i, { field_rules: { ...(t.field_rules || {}), interested_in: v } })} tid={`rule-interested-${i}`} />
              <RuleSelect label="Phone" value={(t.field_rules || {}).phone || "required"} options={PHONE_REQ} onChange={(v) => upd(i, { field_rules: { ...(t.field_rules || {}), phone: v } })} tid={`rule-phone-${i}`} />
              <RuleSelect label="Recruiter Notes" value={(t.field_rules || {}).notes || "optional"} options={FIELD_REQ} onChange={(v) => upd(i, { field_rules: { ...(t.field_rules || {}), notes: v } })} tid={`rule-notes-${i}`} />
              <RuleSelect label="Resume" value={(t.field_rules || {}).resume || "optional"} options={FIELD_REQ} onChange={(v) => upd(i, { field_rules: { ...(t.field_rules || {}), resume: v } })} tid={`rule-resume-${i}`} />
            </div>
            <p className="text-[11px] text-slate-400">Recruiter Notes &amp; Resume rules apply to recruiter-assisted bookings; Interested In &amp; Phone apply to all bookings.</p>
          </div>
          {t.method === "teams" && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`type-teams-${i}`}>
              {p.microsoft_connected ? (
                <p className="text-xs text-emerald-700 flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />A unique Microsoft Teams meeting is generated automatically for each booking via your connected Microsoft 365 calendar.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Microsoft isn't connected. Connect it for automatic Teams meetings, or set a fallback link below. Without either, online bookings won't include a join link.</p>
                  <Button size="sm" variant="outline" className="text-xs h-7" data-testid={`type-connect-ms-${i}`}
                    onClick={async () => { try { const { data } = await api.post("/comm-connection/microsoft/connect"); if (data.authorize_url) window.location.href = data.authorize_url; } catch { toast.error("Could not start Microsoft connection"); } }}>
                    <Video className="w-3.5 h-3.5 mr-1" />Connect Microsoft
                  </Button>
                </div>
              )}
              <Fld label="Fallback Teams/online link (used only when Microsoft isn't connected)">
                <Input value={t.online_link || ""} onChange={(e) => upd(i, { online_link: e.target.value })} placeholder="https://teams.microsoft.com/l/meetup-join/…" data-testid={`type-online-link-${i}`} />
              </Fld>
              <label className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`type-allow-static-${i}`}>
                <span className="text-xs text-slate-600">Allow this static link as a fallback<br /><span className="text-[11px] text-slate-400">When off, online bookings are blocked until the host connects Microsoft (no silent generic link).</span></span>
                <Switch checked={!!t.allow_static_link} onCheckedChange={(v) => upd(i, { allow_static_link: v })} />
              </label>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Active</span>
            <Switch checked={t.active !== false} onCheckedChange={(v) => upd(i, { active: v })} data-testid={`type-active-${i}`} />
          </div>
        </div>
      ))}
      <Button variant="outline" onClick={add} data-testid="type-add"><Plus className="w-4 h-4 mr-1" />Add Appointment Type</Button>
    </Panel>
  );
}

function DayEditor({ day, label, windows, onChange }) {
  const upd = (i, patch) => onChange(windows.map((w, j) => j === i ? { ...w, ...patch } : w));
  const add = () => onChange([...windows, { start: "09:00", end: "17:00" }]);
  const remove = (i) => onChange(windows.filter((_, j) => j !== i));
  return (
    <div className="flex gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`avail-day-${day}`}>
      <div className="w-24 shrink-0 pt-2 text-sm font-medium text-slate-700">{label}</div>
      <div className="flex-1 space-y-2">
        {windows.length === 0 ? <p className="text-sm text-slate-400 pt-2">Unavailable</p> : windows.map((w, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input type="time" value={w.start} onChange={(e) => upd(i, { start: e.target.value })} className="w-32" data-testid={`avail-${day}-start-${i}`} />
            <span className="text-slate-400">–</span>
            <Input type="time" value={w.end} onChange={(e) => upd(i, { end: e.target.value })} className="w-32" data-testid={`avail-${day}-end-${i}`} />
            <button onClick={() => remove(i)} className="text-rose-400 hover:text-rose-600" data-testid={`avail-${day}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={add} className="text-xs text-brand inline-flex items-center gap-1" data-testid={`avail-${day}-add`}><Plus className="w-3 h-3" />Add Time Range</button>
      </div>
    </div>
  );
}

function AvailabilityTab({ p, set }) {
  const av = p.availability || {};
  const setDay = (day, windows) => set({ availability: { ...av, [day]: windows } });
  const copyMonToWeekdays = () => { const mon = av.mon || []; set({ availability: { ...av, tue: [...mon], wed: [...mon], thu: [...mon], fri: [...mon] } }); toast.success("Copied Monday to weekdays"); };
  const clearDay = (day) => setDay(day, []);
  const overrides = p.overrides || [];
  const addOverride = () => set({ overrides: [...overrides, { date: "", available: false, windows: [] }] });
  const updOv = (i, patch) => set({ overrides: overrides.map((o, j) => j === i ? { ...o, ...patch } : o) });
  const removeOv = (i) => set({ overrides: overrides.filter((_, j) => j !== i) });
  return (
    <>
      <Panel title="Weekly Availability" description="Your recurring recruiting hours. Actual booking times still subtract calendar conflicts and buffers.">
        <div className="flex justify-end -mt-2"><Button variant="outline" size="sm" onClick={copyMonToWeekdays} data-testid="avail-copy-mon">Copy Monday to weekdays</Button></div>
        {DOW.map(([day, label]) => (
          <div key={day} className="flex items-start gap-2">
            <div className="flex-1"><DayEditor day={day} label={label} windows={av[day] || []} onChange={(w) => setDay(day, w)} /></div>
            {(av[day] || []).length > 0 && <button onClick={() => clearDay(day)} className="text-xs text-slate-400 hover:text-slate-600 pt-3 shrink-0" data-testid={`avail-clear-${day}`}>Clear</button>}
          </div>
        ))}
      </Panel>
      <Panel title="Date Overrides" description="One-off exceptions for specific dates (holidays, extra hours).">
        {overrides.map((o, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`override-row-${i}`}>
            <div className="flex items-center gap-3">
              <Input type="date" value={o.date} onChange={(e) => updOv(i, { date: e.target.value })} className="w-44" data-testid={`override-date-${i}`} />
              <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={!!o.available} onCheckedChange={(v) => updOv(i, { available: v, windows: v && !(o.windows || []).length ? [{ start: "09:00", end: "17:00" }] : o.windows })} data-testid={`override-available-${i}`} />Available</label>
              <button onClick={() => removeOv(i)} className="text-rose-400 hover:text-rose-600 ml-auto" data-testid={`override-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            {o.available ? <DayEditor day={`ov${i}`} label="Hours" windows={o.windows || []} onChange={(w) => updOv(i, { windows: w })} /> : <p className="text-xs text-slate-400">Unavailable all day</p>}
          </div>
        ))}
        <Button variant="outline" onClick={addOverride} data-testid="override-add"><Plus className="w-4 h-4 mr-1" />Add Date Override</Button>
      </Panel>
    </>
  );
}

function RulesTab({ p, set }) {
  const r = p.rules || {};
  const setR = (patch) => set({ rules: { ...r, ...patch } });
  return (
    <Panel title="Booking Rules" description="Control how far ahead and how often visitors can book.">
      <div className="grid sm:grid-cols-2 gap-4">
        <Fld label="Minimum Notice">
          <Select value={String(r.min_notice_min ?? 240)} onValueChange={(v) => setR({ min_notice_min: Number(v) })}>
            <SelectTrigger data-testid="rule-notice"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{NOTICE.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="Booking Horizon">
          <Select value={String(r.horizon_days ?? 30)} onValueChange={(v) => setR({ horizon_days: Number(v) })}>
            <SelectTrigger data-testid="rule-horizon"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{HORIZON.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="Buffer Before">
          <Select value={String(r.buffer_before_min ?? 0)} onValueChange={(v) => setR({ buffer_before_min: Number(v) })}>
            <SelectTrigger data-testid="rule-buffer-before"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{BUFFER.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="Buffer After">
          <Select value={String(r.buffer_after_min ?? 10)} onValueChange={(v) => setR({ buffer_after_min: Number(v) })}>
            <SelectTrigger data-testid="rule-buffer-after"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{BUFFER.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="Maximum Appointments Per Day">
          <Select value={String(r.daily_limit ?? 0)} onValueChange={(v) => setR({ daily_limit: Number(v) })}>
            <SelectTrigger data-testid="rule-daily-limit"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{[[0, "No limit"], [1, "1"], [2, "2"], [3, "3"], [4, "4"], [5, "5"], [6, "6"], [8, "8"], [10, "10"]].map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
      </div>
    </Panel>
  );
}

function FormTab({ p, set }) {
  const form = p.form || {};
  const optional = form.optional_fields || [];
  const toggle = (key) => set({ form: { ...form, optional_fields: optional.includes(key) ? optional.filter((k) => k !== key) : [...optional, key] } });
  const interests = p.interest_options || [];
  const updI = (i, patch) => set({ interest_options: interests.map((o, j) => j === i ? { ...o, ...patch } : o) });
  const addI = () => set({ interest_options: [...interests, { code: `OPT_${uid().toUpperCase()}`, label: "New option", active: true, order: interests.length + 1, map_type: "none", map_value: "" }] });
  const removeI = (i) => set({ interest_options: interests.filter((_, j) => j !== i) });
  const moveI = (i, d) => { const n = [...interests]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; n.forEach((o, k) => o.order = k + 1); set({ interest_options: n }); };
  return (
    <>
    <Panel title="Booking Form" description="These identity/contact fields are always required for safe matching. Enable extra questions below.">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
        <p className="font-medium text-slate-700 mb-1">Always required</p>
        <div className="flex flex-wrap gap-2">{["First Name", "Last Name", "Email", "Mobile Phone"].map((f) => <span key={f} className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5">{f}</span>)}</div>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-600">Optional questions</p>
        {OPTIONAL_FIELDS.filter(([k]) => k !== "interested_in").map(([key, label]) => (
          <label key={key} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5" data-testid={`form-opt-${key}`}>
            <span className="text-sm text-slate-700">{label}</span>
            <Switch checked={optional.includes(key)} onCheckedChange={() => toggle(key)} />
          </label>
        ))}
      </div>
    </Panel>
    <Panel title="Interested In options" description="Shown as a dropdown on the booking form. Control per-type Required/Optional/Hidden under Appointment Types.">
      {interests.map((o, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`interest-row-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={o.label} onChange={(e) => updI(i, { label: e.target.value })} className="font-medium" data-testid={`interest-label-${i}`} />
            <button onClick={() => moveI(i, -1)} className="text-slate-400 hover:text-slate-600" data-testid={`interest-up-${i}`}><ChevronUp className="w-4 h-4" /></button>
            <button onClick={() => moveI(i, 1)} className="text-slate-400 hover:text-slate-600" data-testid={`interest-down-${i}`}><ChevronDown className="w-4 h-4" /></button>
            <button onClick={() => removeI(i)} className="text-rose-400 hover:text-rose-600" data-testid={`interest-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Fld label="Option code (stable)"><Input value={o.code} onChange={(e) => updI(i, { code: e.target.value })} data-testid={`interest-code-${i}`} /></Fld>
            <Fld label="Maps to (program / candidate_type / none)"><Input value={o.map_value || ""} onChange={(e) => updI(i, { map_value: e.target.value, map_type: e.target.value ? (o.map_type && o.map_type !== "none" ? o.map_type : "program") : "none" })} placeholder="e.g. retail_traditional" data-testid={`interest-map-${i}`} /></Fld>
          </div>
          <label className="flex items-center justify-between"><span className="text-xs text-slate-500">Active</span><Switch checked={o.active !== false} onCheckedChange={(v) => updI(i, { active: v })} data-testid={`interest-active-${i}`} /></label>
        </div>
      ))}
      <Button variant="outline" onClick={addI} data-testid="interest-add"><Plus className="w-4 h-4 mr-1" />Add Interest Option</Button>
    </Panel>
    </>
  );
}

function BrandingTab({ p, set }) {
  const navigate = useNavigate();
  const b = p.branding || {};
  const setB = (patch) => set({ branding: { ...b, ...patch } });
  const [photoOk, setPhotoOk] = useState(true);
  const [stamp] = useState(() => Date.now());
  const useProfile = async () => {
    try {
      const { data } = await api.get("/my-profile");
      setB({
        display_name: data.personal?.display_name || [data.personal?.first_name, data.personal?.last_name].filter(Boolean).join(" ") || b.display_name,
        title: data.work?.title || b.title,
      });
      toast.success("Filled Display Name and Title from My Profile");
    } catch (e) { toast.error(apiError(e)); }
  };
  const initials = (b.display_name || p.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <Panel title="Branding" description={p.is_agency_page ? "How you appear on your public booking page. Your photo comes from My Profile; add your agency name and logo below." : "How you appear on the public page. Your photo comes from My Profile; the logo is account branding."}>
      {!p.microsoft_connected && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2 text-sm text-amber-800" data-testid="ms-notice">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Connect Microsoft Calendar in My Profile to automatically block external calendar conflicts. Scheduling still works without it.</span>
        </div>
      )}
      <div className="flex justify-end -mt-1"><Button variant="outline" size="sm" onClick={useProfile} data-testid="brand-use-profile">Use My Profile Information</Button></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Fld label="Display Name"><Input value={b.display_name || ""} onChange={(e) => setB({ display_name: e.target.value })} placeholder="Inherited from the host's profile" data-testid="brand-name" /></Fld>
        <Fld label="Title"><Input value={b.title || ""} onChange={(e) => setB({ title: e.target.value })} placeholder="Inherited from the host's profile" data-testid="brand-title" /></Fld>
      </div>
      {p.is_agency_page
        ? <Fld label="Agency Name"><Input value={b.agency_name || ""} maxLength={120} onChange={(e) => setB({ agency_name: e.target.value })} placeholder="e.g. Fleming Insurance Agency" data-testid="brand-agency-name" /></Fld>
        : <Fld label="Company"><p className="text-sm text-slate-700" data-testid="brand-company">Farmers Insurance <span className="text-xs text-slate-400">(inherited from account branding)</span></p></Fld>}
      <div className="grid sm:grid-cols-2 gap-3">
        <Fld label="Profile Photo">
          <div className="flex items-center gap-3" data-testid="brand-photo">
            {p.user_id && photoOk
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/team/photo-user/${p.user_id}?v=${stamp}`} alt="" onError={() => setPhotoOk(false)} className="w-14 h-14 rounded-full object-cover border border-slate-200" data-testid="brand-photo-preview" />
              : <div className="w-14 h-14 rounded-full bg-brand/10 text-brand font-semibold flex items-center justify-center" data-testid="brand-photo-initials">{initials}</div>}
            <div>
              <p className="text-xs text-slate-500">Source: My Profile</p>
              <Button variant="outline" size="sm" className="mt-1 h-7 text-xs" onClick={() => navigate("/profile")} data-testid="brand-update-photo">Update Profile Photo</Button>
            </div>
          </div>
        </Fld>
        {p.is_agency_page ? <AgencyLogoField p={p} set={set} /> : (
        <Fld label="Organization Logo">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <img src="/assets/email/farmers-insurance-logo.png" alt="Farmers Insurance" className="h-10 w-auto bg-white rounded border border-slate-200 px-2 py-1" />
            <p className="text-xs text-slate-500">Managed by Top-Recruit / Account Branding</p>
          </div>
        </Fld>)}
      </div>
    </Panel>
  );
}

function AgencyLogoField({ p, set }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/scheduling-pages/${p.id}/logo`, fd);
      set({ logo_url: data.logo_url }); toast.success("Agency logo updated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/scheduling-pages/${p.id}/logo`); set({ logo_url: "" }); toast.success("Agency logo removed"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Fld label="Agency Logo">
      <div className="flex items-center gap-3" data-testid="brand-agency-logo">
        {p.logo_url
          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.logo_url}`} alt="Agency logo" className="h-12 w-auto max-w-[140px] object-contain bg-white rounded border border-slate-200 px-2 py-1" data-testid="brand-agency-logo-preview" />
          : <div className="h-12 w-20 rounded border border-dashed border-slate-300 text-[10px] text-slate-400 flex items-center justify-center">No logo</div>}
        <div className="flex flex-col gap-1">
          <label className="inline-flex">
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="brand-agency-logo-input" />
            <span className="cursor-pointer text-xs font-medium border border-slate-200 rounded-md px-2.5 py-1 hover:bg-slate-50">{busy ? "Uploading…" : p.logo_url ? "Replace Logo" : "Upload Logo"}</span>
          </label>
          {p.logo_url ? <button type="button" onClick={remove} className="text-xs text-red-500 hover:underline text-left" data-testid="brand-agency-logo-remove">Remove</button> : null}
          <p className="text-[11px] text-slate-400">PNG, JPG or WEBP, up to 5 MB</p>
        </div>
      </div>
    </Fld>
  );
}

function NotificationsTab({ p, set }) {
  const n = p.notifications || {};
  const setN = (patch) => set({ notifications: { ...n, ...patch } });
  return (
    <Panel title="Notifications" description="Emails sent when a booking is made. Automated reminders are a later enhancement.">
      <label className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3" data-testid="notif-candidate">
        <div><p className="text-sm font-medium text-slate-700">Prospect confirmation email</p><p className="text-xs text-slate-400">Sends the visitor a confirmation with reschedule/cancel links.</p></div>
        <Switch checked={n.candidate_confirmation !== false} onCheckedChange={(v) => setN({ candidate_confirmation: v })} />
      </label>
      <label className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3" data-testid="notif-recruiter">
        <div><p className="text-sm font-medium text-slate-700">Recruiter notification</p><p className="text-xs text-slate-400">Emails you and adds an in-app notification when someone books.</p></div>
        <Switch checked={n.recruiter_notification !== false} onCheckedChange={(v) => setN({ recruiter_notification: v })} />
      </label>
    </Panel>
  );
}
